import mainMenu from '@/shared/components/layout/menu/configs/main_menu.json';
import userMenu from '@/shared/components/layout/menu/configs/user_menu.json';
import { hasModernPreviewAccess } from '@/shells/models/modernPreviewAccess';
import { availableExperience, contentNavigationItem } from './contentExperience';

// The legacy catalogue remains the source of labels, routes and permission flags.
// `options2` is deliberately excluded: it is not rendered by the current menu.
export const legacyNavigationItems = mainMenu.reduce((items, selector) => {
  if (typeof selector.options[0] === 'string') return items;
  return items.concat(selector.options.length ? selector.options : [selector]);
}, []);

const item = (name, extra = {}) => {
  const source = legacyNavigationItems.find(option => option.url_name === name);
  if (!source) throw new Error(`Missing current navigation destination: ${name}`);
  return { ...source, ...extra, routeName: source.url_name, id: source.url_name };
};
const section = (label, items) => ({ label, items });

export const INCREMENTAL_NAVIGATION = [
  { id: 'chart', label: 'Graficador', item: item('Stockchart', { name: 'Graficador', activePaths: ['/app/graficador-dinamico'] }) },
  { id: 'market', label: 'Mercado', sections: [section('Explorar el mercado', [
    item('Widgets'), item('Directionality'), item('NewsIPOs'),
    item('InteligenceArtificial', { activeNames: ['TwinSouls'] })
  ])] },
  { id: 'portfolio', label: 'Carteras', item: item('Portfolio', { name: 'Carteras', activePaths: ['/app/portfolio'] }) },
  { id: 'tools', label: 'Herramientas', sections: [
    section('Gestión y práctica', [item('Risk'), item('BrokerSimulator'), item('Calculadora de cartera')]),
    section('Calculadoras', [
      item('StopLoss', { name: 'Stop loss' }),
      item('Calculadora de interés compuesto', { name: 'Interés compuesto' }),
      item('Calculadora de autonomía financiera', { name: 'Autonomía financiera' }),
      item('InvestmentCalculator', { name: 'Rentabilidad' }),
      item('Calculadora de inflación', { name: 'Inflación' })
    ])
  ] },
  { id: 'learning', label: 'Formación', sections: [section('Aprender y practicar', [
    item('Exercises'), item('Videos', { name: 'Vídeos' }), item('LibrosBolsa')
  ])] },
  { id: 'news', label: 'Actualidad', sections: [section('Información de bolsa', [
    item('informes', { activeNames: ['informe-detail', 'InformeMain', 'InformeLogin'] }),
    item('NoticiasFlash'),
    item('NewsflashAdmin', { activeNames: ['NewsflashAdminQueueDetail', 'NewsflashAdminJaReview', 'NewsflashAdminJaBatchReview'] })
  ])] },
  { id: 'forum', label: 'Foro', item: item('Forum', { activePaths: ['/app/forum'] }) }
];

const ACCOUNT_ICONS = { MyProfile: 'person_outline', UserPhoto: 'portrait', UserBills: 'receipt_long', UserCertificates: 'workspace_premium', Marketplace: 'shopping_bag' };
export const INCREMENTAL_ACCOUNT_ITEMS = userMenu.options.map(option => ({
  ...option, id: option.url_name, routeName: option.url_name, icon: ACCOUNT_ICONS[option.url_name]
}));
export const HELP_ITEM = item('HelpCenter');

// Preserve the legacy active-role rule, including older role IDs and user flags.
export function hasCurrentActiveRole(user = {}, features = {}) {
  if (features.has_active_role) return true;
  const roles = user.roles || {};
  return [13, 14, 15, 16, 20, 21, 27].some(id => roles[id] !== undefined)
    || ['Bronce', 'Plata', 'Oro', 'Platino', 'Pre Eureker', 'basico_europa', 'basico_america'].some(name => Object.values(roles).includes(name))
    || ['is_bronze_user', 'is_silver_user', 'is_gold_user', 'is_platinum_user', 'is_pre_eureker_user', 'is_pre_user', 'is_basic_user', 'is_basico_asiste_avanzado', 'is_developer_user', 'is_admin_user'].some(flag => Boolean(user[flag]));
}

export function isCurrentItemVisible(option, user = {}, features = {}, settings = {}) {
  const admin = hasModernPreviewAccess(user);
  if (option.admin_only) return admin;
  if (option.active_role_only) {
    if (option.newsflash_public_access_controlled && !settings.newsflashPublicAccessEnabled) return admin;
    return admin || hasCurrentActiveRole(user, features);
  }
  return true;
}

export function currentNavigation(user, features, settings, experience = 'legacy') {
  const version = availableExperience(experience, user);
  return INCREMENTAL_NAVIGATION.map(group => ({
    ...group,
    item: group.item && contentNavigationItem(group.item, version),
    sections: group.sections && group.sections.map(part => ({
      ...part, items: part.items.filter(option => isCurrentItemVisible(option, user, features, settings)).map(option => contentNavigationItem(option, version))
    })).filter(part => part.items.length)
  })).filter(group => group.item ? isCurrentItemVisible(group.item, user, features, settings) : group.sections.length);
}

export function currentAccountExtras(user = {}, features = {}, settings = {}) {
  const links = [];
  if (settings.referralsEnabled && hasCurrentActiveRole(user, features)) links.push({ id: 'referrals', name: 'Referidos', routeName: 'Referrals', icon: 'card_giftcard' });
  if (user.is_basico_asiste_avanzado) links.push({ id: 'register', name: 'Alta Eurekers', href: '/registro/eureker', icon: 'person_add' });
  if (user.is_avanzado_off) links.push({ id: 'reactivate', name: 'Re-Alta Eurekers', href: '/area_avanzado_off', icon: 'person_add' });
  if (user.is_basico_off) links.push({ id: 'services', name: 'Servicios Eurekers · cursos', href: '/curso-de-bolsa', icon: 'school' });
  if (user.is_avanzado_off || user.is_basico_off) links.push({ id: 'courses', name: 'Próximos cursos', routeName: 'Courses', icon: 'event' });
  if (user.is_admin_user) links.push({ id: 'admin-menu', name: 'Admin menú', href: '/admin/structure/menu/manage/main-menu', icon: 'admin_panel_settings' });
  return links;
}

export function isNavigationItemActive(option, route = {}) {
  return option.routeName === route.name
    || (option.activeNames || []).includes(route.name)
    || (option.activePaths || []).some(path => route.path === path || String(route.path || '').startsWith(path + '/'));
}

export function hidesCurrentNavigation(route = {}, loggedIn = false) {
  return ['Login', 'InformeFlow', 'Promo'].includes(route.name)
    || (route.name === 'UserCertificates' && !loggedIn);
}
